import { useMemo, useRef, useState } from "react";
import type { DrawMode, LatLngTuple } from "../../core/options";
import { drawSummary } from "../../draw/Measure";
import { simplifyPoints } from "../../geo/simplify";
import { parseWkt } from "../../geo/wkt";
import { format } from "../../i18n/labels";
import { useDraw, useLabels, useSelection } from "../hooks";
import { Button } from "../primitives/Button";
import { Popover } from "../primitives/Popover";
import { Segmented } from "../primitives/Segmented";
import { Slider } from "../primitives/Slider";

export type DrawToolbarProps = {
  className?: string;
  showWhenIdle?: boolean;
};

export const simplifyDefaultMeters = 5;

export const simplifyRange = { min: 1, max: 200 } as const;

export function mergeSourceOf(objects: { wkt: string | null }[]): LatLngTuple[] | null {
  for (const object of objects) {
    const geometry = parseWkt(object.wkt);

    if (geometry !== null && geometry.kind === "line") return geometry.points;
  }

  return null;
}

export function DrawToolbar({ className, showWhenIdle = true }: DrawToolbarProps) {
  const labels = useLabels();
  const { snapshot, api } = useDraw();
  const { objects } = useSelection();
  const [simplifyOpen, setSimplifyOpen] = useState(false);
  const [tolerance, setTolerance] = useState(simplifyDefaultMeters);
  const simplifyRef = useRef<HTMLButtonElement>(null);
  const { geometry, phase, canUndo, canRedo, snap, editable } = snapshot;
  const mode = geometry.mode;
  const pathMode = mode === "polyline" || mode === "polygon";
  const mergeSource = useMemo(() => mergeSourceOf(objects), [objects]);
  const simplifiedCount = useMemo(() => (simplifyOpen ? simplifyPoints(geometry.points, tolerance).length : geometry.points.length), [simplifyOpen, geometry.points, tolerance]);
  const idle = mode === null;

  if (idle && !showWhenIdle) return null;

  const startMode = (next: DrawMode) => {
    if (mode === next) return;

    api.start(next, { editable: true });
  };

  const closeSimplify = () => setSimplifyOpen(false);

  const applySimplify = () => {
    api.simplify(tolerance);
    closeSimplify();
  };

  const summary: string[] = [];
  const measured = drawSummary(geometry, labels);

  if (geometry.points.length > 0) summary.push(format(labels.drawVertexCount, { count: geometry.points.length }));
  if (measured !== null) summary.push(measured);

  const reason = mode !== null && !geometry.valid && geometry.reason !== null && geometry.points.length > 0 ? geometry.reason : null;

  return (
    <div className={["fxmap-toolbar", "fxmap-card", idle ? "fxmap-toolbar-idle" : "", className ?? ""].filter(Boolean).join(" ")} role="toolbar" aria-label={labels.drawLine} data-fxmap="draw-toolbar">
      <div className="fxmap-toolbar-row">
        <Segmented
          label={labels.drawLine}
          value={mode}
          iconOnly
          className="fxmap-toolbar-modes"
          options={[
            { value: "point", label: labels.drawPoint, icon: "point", title: `${labels.drawPoint} (1)`, action: "mode-point" },
            { value: "polyline", label: labels.drawLine, icon: "line", title: `${labels.drawLine} (2)`, action: "mode-polyline" },
            { value: "polygon", label: labels.drawPolygon, icon: "polygon", title: `${labels.drawPolygon} (3)`, action: "mode-polygon" }
          ]}
          onChange={startMode}
        />
        <span className="fxmap-toolbar-group" role="group" aria-label={labels.drawUndo}>
          <Button kind="icon" icon="undo" label={labels.drawUndo} title={`${labels.drawUndo} (Ctrl+Z)`} action="undo" disabled={!canUndo} onClick={() => api.undo()} />
          <Button kind="icon" icon="redo" label={labels.drawRedo} title={`${labels.drawRedo} (Ctrl+Y)`} action="redo" disabled={!canRedo} onClick={() => api.redo()} />
        </span>
        <span className="fxmap-toolbar-group" role="group" aria-label={labels.drawSimplify}>
          <Button kind="icon" icon="magnet" label={snap ? labels.drawSnap : labels.drawSnapOff} title={`${snap ? labels.drawSnap : labels.drawSnapOff} (Shift)`} action="snap" pressed={snap} onClick={() => api.setSnap(!snap)} />
          <Button kind="icon" icon="reverse" label={labels.drawReverse} action="reverse" disabled={!pathMode || geometry.points.length < 2 || !editable} onClick={() => api.reverse()} />
          <span className="fxmap-popover-host">
            <Button ref={simplifyRef} kind="icon" icon="simplify" label={labels.drawSimplify} action="simplify" aria-haspopup="dialog" aria-expanded={simplifyOpen} disabled={!pathMode || geometry.points.length < 3 || !editable} onClick={() => setSimplifyOpen(!simplifyOpen)} />
            <Popover open={simplifyOpen} onClose={closeSimplify} anchorRef={simplifyRef} label={labels.drawSimplify} dataFxmap="draw-simplify" className="fxmap-toolbar-popover">
              <Slider label={labels.drawSimplifyTolerance} showLabel min={simplifyRange.min} max={simplifyRange.max} step={1} value={tolerance} valueText={value => `${value} m`} onChange={setTolerance} />
              <span className="fxmap-toolbar-popover-count fxmap-tabular fxmap-muted" aria-live="polite">
                {`${format(labels.drawVertexCount, { count: simplifiedCount })} / ${geometry.points.length}`}
              </span>
              <span className="fxmap-toolbar-popover-actions">
                <Button kind="ghost" size="sm" label={labels.cancel} onClick={closeSimplify} action="simplify-cancel" />
                <Button kind="primary" size="sm" label={labels.drawFinish} onClick={applySimplify} action="simplify-apply" />
              </span>
            </Popover>
          </span>
          <Button kind="icon" icon="merge" label={labels.drawMerge} action="merge" disabled={mode !== "polyline" || mergeSource === null || !editable} onClick={() => mergeSource !== null && api.mergeWith(mergeSource)} />
          <Button kind="icon" icon="trash" label={labels.drawDelete} title={`${labels.drawDelete} (Delete)`} action="delete" disabled={mode === null || geometry.points.length === 0 || !editable} onClick={() => api.clear()} />
        </span>
        <span className="fxmap-toolbar-group fxmap-toolbar-finish">
          <Button kind="ghost" size="sm" label={labels.drawCancel} title={`${labels.drawCancel} (Esc)`} action="cancel" disabled={mode === null} onClick={() => api.cancel()} />
          <Button kind="primary" size="sm" label={labels.drawFinish} title={reason ?? `${labels.drawFinish} (Enter)`} action="finish" disabled={mode === null || !geometry.valid || phase === "editing"} onClick={() => api.finish()} />
        </span>
      </div>
      {summary.length > 0 || reason !== null ? (
        <div className={["fxmap-toolbar-summary", reason !== null ? "fxmap-toolbar-summary-invalid" : ""].filter(Boolean).join(" ")} data-fxmap="draw-summary">
          {summary.map(item => (
            <span key={item} className="fxmap-toolbar-summary-item fxmap-tabular">
              {item}
            </span>
          ))}
          {reason !== null ? (
            <span className="fxmap-toolbar-reason" role="status" aria-live="polite">
              {reason}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
